<script lang="ts">
  import { onMount } from 'svelte';
  import type { AppConstant } from '../lib/constants';
  
  // Создадим минимальную версию API для работы с константами
  const constantsApi = {
    async get<T>(url: string): Promise<T> {
      const response = await fetch(`/api${url}`);
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      return response.json();
    },
    
    async post<T>(url: string, data: any): Promise<T> {
      const response = await fetch(`/api${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      return response.json();
    },
    
    async put<T>(url: string, data: any): Promise<T> {
      const response = await fetch(`/api${url}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      return response.json();
    },
    
    async delete(url: string): Promise<void> {
      const response = await fetch(`/api${url}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
    }
  };

  // State
  let constants: AppConstant[] = [];
  let categories: string[] = [];
  let selectedCategory: string = '';
  let isLoading: boolean = true;
  let error: string | null = null;
  let showForm: boolean = false;
  
  // Form state
  let editMode: boolean = false;
  let formData = {
    id: null as number | null,
    category: '',
    key: '',
    value: '',
    description: '',
    is_active: true
  };

  // Fetch all constants on component mount
  onMount(async () => {
    await loadConstants();
  });

  // Load constants from API
  async function loadConstants() {
    isLoading = true;
    error = null;
    
    try {
      constants = await constantsApi.get<AppConstant[]>('/constants');
      
      // Extract unique categories
      const categorySet = new Set<string>();
      constants.forEach(constant => categorySet.add(constant.category));
      categories = Array.from(categorySet).sort();
      
      if (categories.length > 0 && !selectedCategory) {
        selectedCategory = categories[0];
      }
    } catch (err) {
      error = 'Ошибка загрузки констант: ' + (err instanceof Error ? err.message : String(err));
    } finally {
      isLoading = false;
    }
  }

  // Filter constants by selected category
  $: filteredConstants = selectedCategory
    ? constants.filter(c => c.category === selectedCategory)
    : constants;

  // Reset form
  function resetForm() {
    formData = {
      id: null,
      category: selectedCategory || '',
      key: '',
      value: '',
      description: '',
      is_active: true
    };
    editMode = false;
  }

  // Show form for creating a new constant
  function showCreateForm() {
    resetForm();
    showForm = true;
  }

  // Show form for editing an existing constant
  function showEditForm(constant: AppConstant) {
    formData = {
      id: constant.id,
      category: constant.category,
      key: constant.key,
      value: constant.value,
      description: constant.description || '',
      is_active: constant.is_active
    };
    editMode = true;
    showForm = true;
  }

  // Submit form
  async function submitForm() {
    isLoading = true;
    error = null;
    
    try {
      if (editMode && formData.id) {
        // Update existing constant
        const { id, ...updateData } = formData;
        await constantsApi.put<AppConstant>(`/constants/${id}`, updateData);
      } else {
        // Create new constant
        await constantsApi.post<AppConstant>('/constants', formData);
      }
      
      // Reload constants
      await loadConstants();
      showForm = false;
    } catch (err) {
      error = 'Ошибка сохранения константы: ' + (err instanceof Error ? err.message : String(err));
    } finally {
      isLoading = false;
    }
  }

  // Delete constant
  async function deleteConstant(id: number) {
    if (!confirm('Вы уверены, что хотите удалить эту константу?')) {
      return;
    }
    
    isLoading = true;
    error = null;
    
    try {
      await constantsApi.delete(`/constants/${id}`);
      await loadConstants();
    } catch (err) {
      error = 'Ошибка удаления константы: ' + (err instanceof Error ? err.message : String(err));
    } finally {
      isLoading = false;
    }
  }
</script>

<div class="constants-manager">
  <h2>Управление константами</h2>
  
  {#if error}
    <div class="error-message">{error}</div>
  {/if}
  
  <div class="controls">
    <div class="category-filter">
      <label for="category-select">Категория:</label>
      <select 
        id="category-select" 
        bind:value={selectedCategory}
        disabled={isLoading}
      >
        <option value="">Все категории</option>
        {#each categories as category}
          <option value={category}>{category}</option>
        {/each}
      </select>
    </div>
    
    <button 
      class="btn-primary"
      on:click={showCreateForm}
      disabled={isLoading}
    >
      Создать константу
    </button>
  </div>
  
  {#if isLoading && constants.length === 0}
    <div class="loading">Загрузка констант...</div>
  {:else if constants.length === 0}
    <div class="empty-state">
      <p>Константы не найдены. Создайте первую константу, нажав кнопку выше.</p>
    </div>
  {:else}
    <table class="constants-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Категория</th>
          <th>Ключ</th>
          <th>Значение</th>
          <th>Описание</th>
          <th>Активна</th>
          <th>Действия</th>
        </tr>
      </thead>
      <tbody>
        {#each filteredConstants as constant (constant.id)}
          <tr class:inactive={!constant.is_active}>
            <td>{constant.id}</td>
            <td>{constant.category}</td>
            <td>{constant.key}</td>
            <td class="value-cell">{constant.value}</td>
            <td>{constant.description || '-'}</td>
            <td>{constant.is_active ? 'Да' : 'Нет'}</td>
            <td class="actions">
              <button 
                class="btn-edit"
                on:click={() => showEditForm(constant)}
                disabled={isLoading}
              >
                Изменить
              </button>
              <button 
                class="btn-delete"
                on:click={() => deleteConstant(constant.id)}
                disabled={isLoading}
              >
                Удалить
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
  
  {#if showForm}
    <div class="modal-overlay">
      <div class="modal">
        <h3>{editMode ? 'Редактировать константу' : 'Создать константу'}</h3>
        
        <form on:submit|preventDefault={submitForm}>
          <div class="form-group">
            <label for="category">Категория*:</label>
            <input 
              type="text" 
              id="category" 
              bind:value={formData.category}
              required
            />
          </div>
          
          <div class="form-group">
            <label for="key">Ключ*:</label>
            <input 
              type="text" 
              id="key" 
              bind:value={formData.key}
              required
              disabled={editMode}
            />
          </div>
          
          <div class="form-group">
            <label for="value">Значение*:</label>
            <textarea 
              id="value" 
              bind:value={formData.value}
              required
              rows="3"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label for="description">Описание:</label>
            <textarea 
              id="description" 
              bind:value={formData.description}
              rows="2"
            ></textarea>
          </div>
          
          <div class="form-group checkbox">
            <label>
              <input 
                type="checkbox" 
                bind:checked={formData.is_active}
              />
              Активна
            </label>
          </div>
          
          <div class="form-actions">
            <button 
              type="button"
              class="btn-secondary"
              on:click={() => showForm = false}
              disabled={isLoading}
            >
              Отмена
            </button>
            <button 
              type="submit"
              class="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  {/if}
</div>

<style>
  .constants-manager {
    padding: 1rem;
  }
  
  .controls {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
  
  .category-filter {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .constants-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
  }
  
  .constants-table th,
  .constants-table td {
    border: 1px solid #ddd;
    padding: 0.5rem;
    text-align: left;
  }
  
  .constants-table th {
    background-color: #f5f5f5;
    font-weight: bold;
  }
  
  .value-cell {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .actions {
    display: flex;
    gap: 0.5rem;
  }
  
  .inactive {
    background-color: #f8f8f8;
    color: #777;
  }
  
  .error-message {
    color: #d32f2f;
    background-color: #ffebee;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border-radius: 4px;
  }
  
  .loading,
  .empty-state {
    padding: 2rem;
    text-align: center;
    color: #666;
  }
  
  /* Modal styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal {
    background: white;
    padding: 1.5rem;
    border-radius: 4px;
    width: 100%;
    max-width: 600px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  .form-group {
    margin-bottom: 1rem;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 0.25rem;
    font-weight: bold;
  }
  
  .form-group input,
  .form-group textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  
  .form-group.checkbox {
    display: flex;
    align-items: center;
  }
  
  .form-group.checkbox input {
    width: auto;
    margin-right: 0.5rem;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1.5rem;
  }
  
  /* Button styles */
  button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .btn-primary {
    background-color: #1976d2;
    color: white;
  }
  
  .btn-secondary {
    background-color: #e0e0e0;
    color: #333;
  }
  
  .btn-edit {
    background-color: #ffb74d;
    color: #333;
  }
  
  .btn-delete {
    background-color: #ef5350;
    color: white;
  }
</style>